/**
 * Studio Status Filtering Utilities
 * 
 * Filters studios based on their status according to the Airbnb-style status system.
 * Only ACTIVE studios are publicly visible and bookable.
 */

/**
 * Studio Status Enum (matches backend)
 * @enum {string}
 */
export const StudioStatus = {
  DRAFT: 'DRAFT',           // In Erstellung, nicht öffentlich sichtbar
  ACTIVE: 'ACTIVE',         // Öffentlich sichtbar und buchbar
  PAUSED: 'PAUSED',         // Temporär pausiert vom Host
  INACTIVE: 'INACTIVE',     // Vom Host deaktiviert
  OWNER_DELETED: 'OWNER_DELETED', // Account des Studio-Owners wurde gelöscht
  SUSPENDED: 'SUSPENDED'    // Von Admin/Plattform gesperrt
};

/**
 * Filter studios to only show publicly visible ones (ACTIVE status)
 * This is used in:
 * - HomeScreen.tsx - Hauptfeed
 * - SearchResultScreen.tsx - Suchergebnisse
 * - FavoriteScreen.tsx - Wishlist (shows all saved studios, including INACTIVE)
 * 
 * @param {Array} studios - Array of studio objects
 * @returns {Array} Filtered array containing only ACTIVE studios
 */
export const filterPublicStudios = (studios) => {
  if (!studios || !Array.isArray(studios)) {
    return [];
  }

  return studios.filter(studio => studio.status === StudioStatus.ACTIVE);
};

/**
 * Check if a studio is publicly visible and bookable
 * @param {Object} studio - Studio object
 * @returns {boolean} True if studio is ACTIVE
 */
export const isStudioPublic = (studio) => {
  if (!studio) return false;
  return studio.status === StudioStatus.ACTIVE;
};

/**
 * Check if a studio is deleted (owner account deleted)
 * @param {Object} studio - Studio object
 * @returns {boolean} True if studio status is OWNER_DELETED
 */
export const isStudioOwnerDeleted = (studio) => {
  if (!studio) return false;
  return studio.status === StudioStatus.OWNER_DELETED;
};

/**
 * Get a user-friendly status message for a studio
 * @param {Object} studio - Studio object
 * @returns {string|null} Status message or null if studio is active
 */
export const getStudioStatusMessage = (studio) => {
  if (!studio) return null;
  
  const status = studio.status;

  switch (status) {
    case StudioStatus.DRAFT:
      return 'Dieses Studio ist noch in Erstellung.';
    case StudioStatus.PAUSED:
      return 'Dieses Studio ist vorübergehend pausiert.';
    case StudioStatus.INACTIVE:
      return 'Derzeit nicht verfügbar';
    case StudioStatus.OWNER_DELETED:
      return 'Studio nicht mehr verfügbar - Dieses Studio wurde vom Besitzer deaktiviert.';
    case StudioStatus.SUSPENDED:
      return 'Dieses Studio wurde von der Plattform gesperrt.';
    case StudioStatus.ACTIVE:
    default:
      return null; // No message for active studios
  }
};

/**
 * Check if a studio can be booked
 * Only ACTIVE studios can be booked
 * @param {Object} studio - Studio object
 * @returns {boolean} True if studio can be booked
 */
export const canBookStudio = (studio) => {
  if (!studio) return false;
  return studio.status === StudioStatus.ACTIVE;
};

